import { Localized } from "@fluent/react";
import {
    Alert,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Radio,
    RadioGroup,
    Typography,
} from "@mui/material";
import { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { AppId } from "../../../../app/appTypes";
import { showError, showSuccess } from "../../../notifications/notificationsSlice";
import { CancelButton } from "../../../../app/components/CancelButton";
import { DocumentDqMapping, generateDqMappingsFromAI, prepareDqMappingsGenerationFromAI } from "../documentDqMappingsApi";
import { GenerateButton } from "../../../../app/components/GenerateButton";
import { selectBlobsByResourceId } from "../../attachementsSlice";
import { selectDocument } from "../../documentSlice";
import { impossible } from "../../../../app/Utils";
import FindInPageIcon from "@mui/icons-material/FindInPage";

export interface GenerateDqMappingsFromAIProps {
    documentId: AppId;
    sectionId: string;
    mappingId: number;
    onSuccessfulSave: (mappings: DocumentDqMapping) => void;
    onCancel: () => void;
}

export const GenerateDqMappingsFromAI = ({
    documentId,
    sectionId,
    mappingId,
    onSuccessfulSave,
    onCancel,
}: GenerateDqMappingsFromAIProps) => {
    const dispatch = useAppDispatch();
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState("");
    const { header } = useAppSelector(selectDocument)?.doc ?? impossible()
    const [selectedBlobId, setSelectedBlobId] = useState<string | null>(null);

    const blobs = useAppSelector(state => selectBlobsByResourceId(state, header.guid))
    const hasBlobs = blobs?.blobs && blobs.blobs.length > 0;
    const onChangeSelectedBlob = (blobId: string) => {
        setSelectedBlobId(blobId);
    };

    const handleConfirmDeleteClick = async () => {
        setSaving(true);
        if (!selectedBlobId) {
            setServerError("document-dq-mapping-no-attachment-selected-error");
            setSaving(false);
            return;
        }
        try {
            const prepareResult = await dispatch(prepareDqMappingsGenerationFromAI({ documentId, sectionId, blobId: selectedBlobId }));

            if (prepareDqMappingsGenerationFromAI.fulfilled.match(prepareResult)) {

                const result = await dispatch(generateDqMappingsFromAI({ documentId, sectionId, mappingId: mappingId, blobId: selectedBlobId }));

                if (generateDqMappingsFromAI.fulfilled.match(result)) {
                    dispatch(showSuccess("sucess"));
                    onSuccessfulSave(result.payload);
                }
            } else {
                let errorMsg = "error";
                if (prepareResult.payload && prepareResult.payload.kind === "http") {
                    const problem = prepareResult.payload.problem;
                    if (problem) {
                        errorMsg = problem.title;
                    }
                }
                setServerError(errorMsg);
            }
        } catch (error) {
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="md">
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    bgcolor: "grey.200",
                    color: "text.primary",
                    fontWeight: "bold",
                }}
            >
                <FindInPageIcon color="info" />
                <Localized id="document-dq-mapping-find-in-attachments">
                    <span>_find in attachments</span>
                </Localized>
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {hasBlobs && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    <Localized id="choose-file-to-search">
                                        <span>_Wybierz plik do przeszukania</span>
                                    </Localized>
                                </Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <RadioGroup
                                    value={selectedBlobId ?? ""}
                                    onChange={(e) => onChangeSelectedBlob(e.target.value)}
                                >
                                    {blobs?.blobs?.map((b) => (
                                        <FormControlLabel
                                            key={b.blobId}
                                            value={b.blobId}
                                            control={<Radio size="small" />}
                                            label={
                                                <Typography variant="body2" color="text.secondary">
                                                    {b.fileMeta.fileName}
                                                </Typography>
                                            }
                                        />
                                    ))}
                                </RadioGroup>
                            </Box>
                        </Box>
                    )}

                    {!hasBlobs && (
                        <Alert severity="warning">
                            <Localized id="document-dq-mapping-no-attachments-warning">
                                <span>_no attachments available for this document</span>
                            </Localized>
                        </Alert>
                    )}

                </Box>

                {serverError && (
                    <Alert
                        sx={{ mt: 2 }}
                        onClose={() => {
                            setServerError("");
                        }}
                        severity="error"
                    >
                        <Localized id={serverError}>
                            <span>Server error</span>
                        </Localized>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2, justifyContent: "flex-end" }}>
                <GenerateButton loading={saving} onClick={handleConfirmDeleteClick} />
                <CancelButton loading={saving} onClick={onCancel} />
            </DialogActions>
        </Dialog>
    );
};
